/**
 * A work's photographs: listing them, signing them so they render, and
 * uploading the ones a collector chose.
 *
 * One of two paths in this app that go to Supabase Storage directly rather
 * than through the ONDO API — `provenanceDocuments.ts` is the other — because
 * the question is bytes and not policy: the client carries the caller's JWT
 * and the bucket's policies refuse exactly where a proxy would. Every grid of
 * works signs its thumbnails through `signWorkThumbnails` rather than
 * assembling the round trip itself.
 *
 * The row is inserted before the bytes go up, because `object_path` is
 * generated from the minted id. Nothing here proposes a path, and the storage
 * policies depend on that.
 */

import type { ImageKind } from "@ondo/shared";

import { ApiError } from "./api.ts";
import { supabase } from "./supabase.ts";

/** The bucket the storage policies in 20260804090700 are written against. */
const BUCKET = "works";

/**
 * How long a signed image URL stays good for.
 *
 * Long enough to render a screen and leave it open, short enough that a URL
 * pulled out of the DOM is not a share. The bucket is private for the same
 * reason a collection is: a photograph of a work discloses that the work exists
 * and what it looks like, and that follows a grant rather than a link.
 */
const IMAGE_URL_TTL_SECONDS = 600;

export interface UploadableImage {
  file: File;
  kind: ImageKind;
  /** The custom label for a detail or an 'other'. Null everywhere else. */
  label?: string | null;
}

/** One photograph of a work, as the table holds it. */
export interface WorkPhotograph {
  id: string;
  kind: ImageKind;
  /** The collector's own words for this view. Null when the kind says it all. */
  label: string | null;
  objectPath: string;
}

/**
 * Every photograph of one work, in the order the record puts them in.
 *
 * A direct select rather than an API call, and the reason is the same one the
 * upload gives: `work_images_read` authorises off `visible_work_ids()`, so this
 * client carrying the user's own JWT is refused exactly where a Nest endpoint
 * proxying it would be. `GET /v1/works/:id/images` is declared in @ondo/shared
 * and does not exist; when it is built this function is what it replaces.
 *
 * Ordered by position then created_at — the same ordering the read API uses to
 * pick the one photograph a card shows, so the first thumbnail here and the
 * picture on the collection card are always the same photograph.
 */
export async function fetchWorkImages(workId: string): Promise<WorkPhotograph[]> {
  const { data, error } = await supabase
    .schema("ondo")
    .from("work_images")
    .select("id, kind, label, object_path")
    .eq("work_id", workId)
    .order("position")
    .order("created_at");

  if (error) throw new ApiError(403, `Could not read the photographs: ${error.message}`);

  return data.flatMap((row) =>
    // A row whose generated path is somehow null has no bytes to point at.
    // Dropping it beats rendering a tile that can never resolve.
    row.object_path === null
      ? []
      : [{ id: row.id, kind: row.kind, label: row.label, objectPath: row.object_path }],
  );
}

/**
 * Signs a whole set of photographs in one round trip.
 *
 * The record shows every view at once, so signing them one at a time would be
 * one request per thumbnail. Paths missing from the returned map are paths
 * storage refused — see `signWorkImage` for why that is a state rather than a
 * fault.
 */
export async function signWorkImages(paths: string[]): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map();

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, IMAGE_URL_TTL_SECONDS);

  if (error || !data) return new Map();

  return new Map(
    data.flatMap((row) =>
      row.path === null || row.signedUrl === null ? [] : [[row.path, row.signedUrl] as const],
    ),
  );
}

/**
 * The card photographs for a set of works, keyed by WORK id.
 *
 * What every grid of works needs and none of them should assemble itself: the
 * paths come off the summaries, the signing is one round trip, and a work whose
 * bytes storage refused is simply absent from the map — the card then shows its
 * placeholder rather than failing the screen. See `signWorkImage` for why being
 * refused is a state and not a fault.
 */
export async function signWorkThumbnails(
  works: { id: string; imagePath: string | null }[],
): Promise<Map<string, string>> {
  const photographed = works.flatMap((work) =>
    work.imagePath === null ? [] : [{ id: work.id, path: work.imagePath }],
  );

  const byPath = await signWorkImages(photographed.map((work) => work.path));

  return new Map(
    photographed.flatMap(({ id, path }) => {
      const url = byPath.get(path);
      return url === undefined ? [] : [[id, url] as const];
    }),
  );
}

/**
 * Attaches photographs to a work.
 *
 * These hung off a draft registration until 20260804090700 removed the
 * submission that stood between a collector and their own catalogue. Nothing
 * else about them changed, which is the argument for the shape having been
 * right: an image belongs to the thing it is a picture of.
 *
 * One of two data paths in this app that do not go through the ONDO API, and
 * the reason is bytes rather than policy: a 50MB photograph proxied through
 * Nest would be buffered twice for no gain, when Supabase Storage will take it
 * directly. The authorisation is identical either way — this client carries the
 * user's own JWT, so `work_images_insert` and the bucket's `work_images_upload`
 * policy decide, exactly as they would have if the API had made the same call
 * on the caller's behalf.
 *
 * Order matters. The row is inserted first because `object_path` is a GENERATED
 * column — 'work/<work_id>/<image_id>' — so the path the bytes go to is only
 * knowable once the database has minted the id. Nothing here proposes a path,
 * which is the property the storage policies depend on.
 *
 * Sequential, not `Promise.all`. Uploads that fail want to fail one at a time,
 * with a message naming the file, and a collector on a hotel connection is
 * better served by one photograph at a time finishing than by six stalling
 * together.
 */
export async function signWorkImage(objectPath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(objectPath, IMAGE_URL_TTL_SECONDS);

  // Null rather than a throw, because being refused the bytes is a state and
  // not a fault. Storage authorises off the same grants the API does, so a
  // caller who may read a work through a work grant but not reach into its
  // collection's folder lands here — and the card shows its placeholder
  // instead of failing the screen around it.
  return error ? null : data.signedUrl;
}

export async function uploadWorkImages(
  workId: string,
  images: UploadableImage[],
): Promise<void> {
  const db = supabase.schema("ondo");

  for (const [position, image] of images.entries()) {
    const { data, error } = await db
      .from("work_images")
      .insert({
        work_id: workId,
        kind: image.kind,
        label: image.label ?? null,
        content_type: image.file.type,
        byte_size: image.file.size,
        position,
      })
      .select("id, object_path")
      .single();

    if (error) throw new ApiError(403, `Could not attach ${image.file.name}: ${error.message}`);
    if (!data.object_path) {
      throw new ApiError(500, `Could not attach ${image.file.name}: no object path was returned.`);
    }

    const upload = await supabase.storage
      .from(BUCKET)
      .upload(data.object_path, image.file, { contentType: image.file.type });

    if (upload.error) {
      // A row pointing at bytes that were never written would show the
      // reviewing authority a photograph that 404s. Drop it and surface the
      // failure — the submission has not been sent yet, so a retry is cheap.
      await db.from("work_images").delete().eq("id", data.id);
      throw new ApiError(502, `Could not upload ${image.file.name}: ${upload.error.message}`);
    }
  }
}
